/**
 * Helpers de reconexão da sala (jitter, grace do SDK, saída forçada).
 */

/** Tentativas nativas do socket antes de interferir com soft reconnect. */
export const SDK_SOCKET_RECONNECT_GRACE_ATTEMPTS = 10;

/** Enquanto a aba está escondida, não forçar leaveRoom/rejoin (mata sessão cedo). */
export const HIDDEN_TAB_RECONNECT_DELAY_MS = 45_000;

/** Grace após socket disconnected — dar tempo ao SDK reconectar sozinho. */
export const SOCKET_DISCONNECTED_GRACE_MS_MOBILE = 22_000;
export const SOCKET_DISCONNECTED_GRACE_MS_DESKTOP = 50_000;

/** Grace quando socket está em reconnecting nativo do SDK. */
export const SOCKET_RECONNECTING_GRACE_MS_MOBILE = 32_000;
export const SOCKET_RECONNECTING_GRACE_MS_DESKTOP = 65_000;

/**
 * roomLeft com ICE/PC ainda connected: a mídia está ok; esperamos mais antes
 * de leaveRoom+rejoin para não matar sessão por oscilação de signaling.
 */
export const ROOM_LEFT_WITH_MEDIA_CONNECTED_DELAY_MS = 22_000;
export const ROOM_LEFT_WITH_MEDIA_CONNECTED_DELAY_MS_DESKTOP = 90_000;

/** Reavalia se a mídia caiu antes de forçar soft reconnect (signaling-only). */
export const PASSIVE_RECOVERY_CHECK_MS_DESKTOP = 15_000;
export const PASSIVE_RECOVERY_CHECK_MS_MOBILE = 15_000;

/**
 * Teto para manter a sessão com signaling caído e mídia aparentemente ok.
 *
 * NÃO ENCURTAR sem medir `softReconnectStart` em produção. Já ficou em 12s e o
 * resultado foi 10x mais rejoins forçados (0,14 -> 1,43 por sala-dia), com
 * piora grave relatada pelos usuários: o `leaveRoom()` do cliente antigo viaja
 * pelo signaling — que está caído justamente aí —, então o peer velho continua
 * vivo no SFU e o usuário aparece duplicado, com vídeo rodando e eco de áudio.
 *
 * A reconexão nativa do SDK reaproveita o mesmo peer e não cria fantasma, então
 * esperar é quase sempre melhor que reconectar. p90 ≈ 25s: este teto fica bem
 * acima disso e só pega a cauda realmente travada. O caso "socket failed" não
 * depende deste teto — ali o SDK desistiu e escalamos na hora.
 *
 * Quando a janela estoura (ou a mídia já morreu), o primeiro degrau é
 * `restartIce()` no peer atual — não encurtar este teto. O leave+rejoin só
 * entra se o ICE restart não existir, falhar ou não recuperar a mídia.
 */
export const MAX_SIGNALING_LOSS_PRESERVE_MS = 90_000;

/**
 * Teto de leave+rejoin por cliente. Acima disso paramos de sair da sala
 * (cada leave com signaling caído deixa um peer fantasma) e ficamos em
 * "reconectando" até a janela escorregar.
 */
export const FULL_REJOIN_WINDOW_MS = 120_000;
export const FULL_REJOIN_MAX_PER_WINDOW = 10;

/** Backoff só do leave+rejoin. Não encurta a janela de preservação. */
export const FULL_REJOIN_BACKOFF_BASE_MS = 2_000;
export const FULL_REJOIN_BACKOFF_CAP_MS = 30_000;

export function fullRejoinCountInWindow(
    timestamps: readonly number[],
    nowMs: number,
    windowMs: number = FULL_REJOIN_WINDOW_MS,
): number {
    return timestamps.reduce(
        (count, ts) => (nowMs - ts < windowMs ? count + 1 : count),
        0,
    );
}

export function canAttemptFullRejoin(
    timestamps: readonly number[],
    nowMs: number,
): boolean {
    return fullRejoinCountInWindow(timestamps, nowMs) < FULL_REJOIN_MAX_PER_WINDOW;
}

/** Mantém só as marcas da janela e acrescenta a tentativa que vai sair da sala. */
export function recordFullRejoin(timestamps: readonly number[], nowMs: number): number[] {
    const recent = timestamps.filter((ts) => nowMs - ts < FULL_REJOIN_WINDOW_MS);
    recent.push(nowMs);
    return recent;
}

/**
 * Backoff exponencial com jitter de ±30%. `jitterUnit` em [0, 1]:
 * 0 → −30%, 1 → +30%, 0.5 → sem desvio. Injetável para o teste ser estável.
 */
export function fullRejoinBackoffMs(
    attemptIndex: number,
    jitterUnit: number,
    baseMs: number = FULL_REJOIN_BACKOFF_BASE_MS,
    capMs: number = FULL_REJOIN_BACKOFF_CAP_MS,
): number {
    const exponent = Math.max(0, Math.floor(attemptIndex));
    const raw = baseMs * 2 ** exponent;
    const capped = Math.min(capMs, raw);
    const unit = Math.min(1, Math.max(0, jitterUnit));
    const factor = 0.7 + unit * 0.6;
    return Math.min(capMs, Math.round(capped * factor));
}

export type ReconnectAction = "ice-restart" | "full-rejoin" | "hold";

/**
 * ICE restart reaproveita o peer. leave+rejoin só depois que esse degrau
 * faltou ou falhou, e nunca em loop quando a janela de 10/120s estourou.
 */
export function nextReconnectAction(options: {
    iceRestartAvailable: boolean;
    iceRestartAlreadyFailed: boolean;
    fullRejoinTimestamps: readonly number[];
    nowMs: number;
}): ReconnectAction {
    if (options.iceRestartAvailable && !options.iceRestartAlreadyFailed) {
        return "ice-restart";
    }
    if (canAttemptFullRejoin(options.fullRejoinTimestamps, options.nowMs)) {
        return "full-rejoin";
    }
    return "hold";
}

/**
 * ICE "failed" não volta por consent check — escalar na hora.
 * "disconnected" ainda pode se recuperar; mantém o grace pedido.
 */
export function recoveryDelayForMediaState(
    mediaState: string | null | undefined,
    disconnectedGraceMs: number,
): number {
    if (mediaState === "failed") {
        return 0;
    }
    return disconnectedGraceMs;
}

export function isMediaTransportHealthy(
    snap: {
        iceConnectionState?: string | null;
        connectionState?: string | null;
    } | null | undefined,
): boolean {
    if (!snap) {
        return false;
    }
    const ice = snap.iceConnectionState;
    const pc = snap.connectionState;
    return (
        ice === "connected" ||
        ice === "completed" ||
        pc === "connected"
    );
}

/**
 * Signaling caiu mas WebRTC segue ok e a reunião existe no Firestore — não
 * fazer leaveRoom+rejoin (causa raiz das quedas a cada ~1–2 min no RTK).
 *
 * `signalingLossElapsedMs` limita a preservação: ver MAX_SIGNALING_LOSS_PRESERVE_MS.
 * Omitir o parâmetro mantém o comportamento antigo (sem teto).
 */
export function shouldPreserveSessionDespiteSignalingLoss(
    mediaStillConnected: boolean,
    meetingDocExists: boolean,
    signalingLossElapsedMs?: number | null,
): boolean {
    if (!mediaStillConnected || !meetingDocExists) {
        return false;
    }
    if (
        typeof signalingLossElapsedMs === "number" &&
        signalingLossElapsedMs >= MAX_SIGNALING_LOSS_PRESERVE_MS
    ) {
        return false;
    }
    return true;
}

/**
 * Tempo de signaling caído, a partir do instante registrado no primeiro evento
 * de perda. Sem marca (`null`), devolve 0 — ainda dentro da janela.
 */
export function signalingLossElapsedMs(
    lossSinceMs: number | null | undefined,
    nowMs: number = Date.now(),
): number {
    if (typeof lossSinceMs !== "number") {
        return 0;
    }
    return Math.max(0, nowMs - lossSinceMs);
}

/**
 * Com a mídia comprovadamente parada não há sessão para proteger: encurta o
 * grace para a sala voltar em segundos em vez de ~1 min.
 */
export const DEAD_MEDIA_RECOVERY_DELAY_MS = 2_000;

export function recoveryDelayForSignalingLoss(
    requestedMs: number,
    options: { mediaStillConnected: boolean; signalingLossElapsedMs: number },
): number {
    const preserveWindowOver =
        options.signalingLossElapsedMs >= MAX_SIGNALING_LOSS_PRESERVE_MS;
    if (!options.mediaStillConnected || preserveWindowOver) {
        return Math.min(requestedMs, DEAD_MEDIA_RECOVERY_DELAY_MS);
    }
    return requestedMs;
}

/** Jitter determinístico por usuário para espalhar soft reconnects (0..maxMs). */
export function reconnectJitterMs(userId: string | undefined | null, maxMs = 3000): number {
    if (!maxMs || maxMs <= 0) {
        return 0;
    }
    if (!userId) {
        return Math.floor(Math.random() * maxMs);
    }
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
        hash = (hash * 31 + userId.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % maxMs;
}

/**
 * Anti-stampede em quedas gerais (roomLeft=ended para todos):
 * moderador/dono reconecta primeiro; participantes esperam offset + jitter maior.
 * Não altera keep-alive / segundo plano — só espalha o soft reconnect.
 */
export const STAMPEDE_JITTER_MS_MODERATOR = 2_000;
export const STAMPEDE_JITTER_MS_PARTICIPANT = 15_000;
/** Participantes esperam o moderador estabilizar antes de pedir token novo. */
export const STAMPEDE_PARTICIPANT_BASE_OFFSET_MS = 8_000;

export function reconnectStampedeExtraMs(options: {
    userId?: string | null;
    isModerator: boolean;
}): number {
    const { userId, isModerator } = options;
    if (isModerator) {
        return reconnectJitterMs(userId, STAMPEDE_JITTER_MS_MODERATOR);
    }
    return (
        STAMPEDE_PARTICIPANT_BASE_OFFSET_MS +
        reconnectJitterMs(userId, STAMPEDE_JITTER_MS_PARTICIPANT)
    );
}

/**
 * Só expulsa imediatamente em kick/reject, ou em ended quando a reunião já
 * não existe no Firestore. Encerrar/zerar horas apaga o doc de propósito:
 * nesse caso a saída é obrigatória, mesmo com WebRTC ainda connected.
 */
export function shouldForceExitOnRoomLeft(
    state: string | undefined | null,
    meetingDocExists: boolean,
    mediaStillConnected = false,
): boolean {
    if (!state) {
        return false;
    }
    if (state === "kicked" || state === "rejected") {
        return true;
    }
    if (state === "ended") {
        return !meetingDocExists;
    }
    return false;
}

export function isDocumentHidden(): boolean {
    return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/** Em segundo plano, alonga o delay para não derrubar a sessão com rejoin agressivo. */
export function reconnectDelayForVisibility(requestedMs: number): number {
    if (isDocumentHidden()) {
        return Math.max(requestedMs, HIDDEN_TAB_RECONNECT_DELAY_MS);
    }
    return requestedMs;
}

/**
 * Ao voltar do 2º plano com socket caído mas WebRTC ainda saudável:
 * esperar o SDK recuperar o signaling — não disparar soft reconnect cedo
 * (leaveRoom + isReconnect kick).
 */
export function shouldWaitForSdkAfterVisibilityResume(options: {
    socketConnected: boolean;
    mediaTransportHealthy: boolean;
    meetingDocExists: boolean;
}): boolean {
    if (options.socketConnected) {
        return false;
    }
    return options.mediaTransportHealthy && options.meetingDocExists;
}
